import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type CommonProps = {
  orderId: string;
  trigger: React.ReactNode;
};

function useActionState() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return { navigate, open, setOpen, busy, setBusy };
}

async function postJson(
  url: string,
  init: { method: "POST" | "PATCH" | "PUT"; body?: unknown },
): Promise<{ ok: boolean; error?: string; code?: string; status?: number }> {
  try {
    const res = await fetch(url, {
      method: init.method,
      credentials: "include",
      headers: init.body ? { "Content-Type": "application/json" } : undefined,
      body: init.body ? JSON.stringify(init.body) : undefined,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
      return { ok: false, error: body.error ?? "Request failed", code: body.code, status: res.status };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: (err as Error).message ?? "Network error" };
  }
}

function handleStaleResponse(
  r: { code?: string; status?: number },
  navigate: ReturnType<typeof useNavigate>,
): boolean {
  if (r.status === 409 && r.code === "stale") {
    toast.message("This order was updated by someone else. Refreshing.");
    navigate(0);
    return true;
  }
  return false;
}

/* ------------------------------ Complete -------------------------------- */

export function CompleteOrderDialog({ orderId, trigger, hasDelivery }: CommonProps & { hasDelivery: boolean }) {
  const { navigate, open, setOpen, busy, setBusy } = useActionState();

  async function onConfirm() {
    setBusy(true);
    const r = await postJson(`/api/admin/orders/${orderId}/complete`, { method: "POST" });
    setBusy(false);
    if (!r.ok) { toast.error(r.error ?? "Could not complete order"); return; }
    toast.success("Order marked complete — customer notified.");
    setOpen(false);
    navigate(0);
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={trigger as React.ReactElement} />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Mark order complete?</AlertDialogTitle>
          <AlertDialogDescription>
            {hasDelivery
              ? "We'll email the customer that their build is ready, with a link to the Build Room."
              : "Save the delivery details first — there's nothing for the customer to open yet."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={busy || !hasDelivery}>
            {busy ? "Sending…" : "Send delivery email"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ------------------------------- Refund --------------------------------- */

export function RefundOrderDialog({ orderId, trigger }: CommonProps) {
  const { navigate, open, setOpen, busy, setBusy } = useActionState();
  const [reason, setReason] = useState("");

  async function onConfirm() {
    setBusy(true);
    const r = await postJson(`/api/admin/orders/${orderId}/refund`, { method: "POST", body: reason.trim() ? { reason: reason.trim() } : {} });
    setBusy(false);
    if (!r.ok) { toast.error(r.error ?? "Refund failed"); return; }
    toast.success("Refund issued — customer notified.");
    setOpen(false);
    setReason("");
    navigate(0);
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={trigger as React.ReactElement} />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Refund this order?</AlertDialogTitle>
          <AlertDialogDescription>We&apos;ll issue a full refund through Stripe and cancel the order.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="refund-reason">Reason (optional)</Label>
          <Textarea id="refund-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Shown to the customer in the cancellation email." rows={3} disabled={busy} />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={busy} variant="destructive">
            {busy ? "Refunding…" : "Refund and cancel"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ------------------------------ Cancel ---------------------------------- */

export function CancelOrderDialog({ orderId, trigger, isPaid }: CommonProps & { isPaid: boolean }) {
  const { navigate, open, setOpen, busy, setBusy } = useActionState();
  const [reason, setReason] = useState("");

  async function onConfirm() {
    setBusy(true);
    const r = await postJson(`/api/admin/orders/${orderId}/cancel`, { method: "POST", body: reason.trim() ? { reason: reason.trim() } : {} });
    setBusy(false);
    if (!r.ok) { toast.error(r.error ?? "Cancel failed"); return; }
    toast.success("Order cancelled — customer notified.");
    setOpen(false);
    setReason("");
    navigate(0);
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={trigger as React.ReactElement} />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Cancel this order?</AlertDialogTitle>
          <AlertDialogDescription>
            {isPaid
              ? "This cancels the order without refunding through Stripe. For the standard path, use Refund instead."
              : "No payment was processed, so nothing to refund."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="cancel-reason">Reason (optional)</Label>
          <Textarea id="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Shown to the customer in the cancellation email." rows={3} disabled={busy} />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Keep order</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={busy} variant="destructive">
            {busy ? "Cancelling…" : "Cancel order"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* --------------------------- Extend deadline ---------------------------- */

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ExtendDeadlineDialog({ orderId, trigger, currentDeadline }: CommonProps & { currentDeadline: string }) {
  const { navigate, open, setOpen, busy, setBusy } = useActionState();
  const [value, setValue] = useState(() => toLocalInputValue(new Date(currentDeadline)));
  const [reason, setReason] = useState("");

  async function onConfirm() {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) { toast.error("Invalid date"); return; }
    if (!reason.trim()) { toast.error("Please provide a reason for the deadline change"); return; }
    setBusy(true);
    const r = await postJson(`/api/admin/orders/${orderId}/deadline`, { method: "PATCH", body: { deadlineAt: parsed.toISOString(), reason: reason.trim() } });
    setBusy(false);
    if (!r.ok) { toast.error(r.error ?? "Could not update deadline"); return; }
    toast.success("Deadline updated — customer notified.");
    setOpen(false);
    setReason("");
    navigate(0);
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={trigger as React.ReactElement} />
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Update delivery deadline</AlertDialogTitle>
          <AlertDialogDescription>We always email the customer when the deadline shifts.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="deadline-input">New deadline</Label>
            <Input id="deadline-input" type="datetime-local" value={value} onChange={(e) => setValue(e.target.value)} disabled={busy} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="deadline-reason">Reason</Label>
            <Textarea id="deadline-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why the deadline is moving — included in the email." rows={3} disabled={busy} />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={busy || !reason.trim()}>
            {busy ? "Saving…" : "Update and notify"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* --------------------------- Start build -------------------------------- */

export function StartBuildButton({ orderId, className }: { orderId: string; className?: string }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  async function onClick() {
    setBusy(true);
    const r = await postJson(`/api/admin/orders/${orderId}/status`, {
      method: "PATCH",
      body: { status: "IN_PROGRESS", expectedStatus: "PENDING" },
    });
    setBusy(false);
    if (!r.ok) {
      if (handleStaleResponse(r, navigate)) return;
      toast.error(r.error ?? "Could not start build");
      return;
    }
    toast.success("Build started — clock is ticking.");
    navigate(0);
  }

  return (
    <Button onClick={onClick} disabled={busy} className={className}>
      {busy ? "Starting…" : "Start build"}
    </Button>
  );
}

/* --------------------------- Reopen ------------------------------------ */

export function ReopenOrderDialog({ orderId, trigger }: CommonProps) {
  const { navigate, open, setOpen, busy, setBusy } = useActionState();

  async function onConfirm() {
    setBusy(true);
    const r = await postJson(`/api/admin/orders/${orderId}/status`, { method: "PATCH", body: { status: "IN_REVIEW", expectedStatus: "COMPLETED" } });
    setBusy(false);
    if (!r.ok) { toast.error(r.error ?? "Could not reopen order"); return; }
    toast.success("Order reopened — back in review.");
    setOpen(false);
    navigate(0);
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={trigger as React.ReactElement} />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reopen this order?</AlertDialogTitle>
          <AlertDialogDescription>
            Moves the order back to <strong>In review</strong>. The customer was already notified the build is ready.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={busy}>
            {busy ? "Reopening…" : "Reopen order"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* --------------------------- Status flip button ------------------------- */

export function StatusFlipButton({
  orderId,
  from,
  to,
  label,
  variant = "outline",
}: {
  orderId: string;
  from: "PENDING" | "IN_PROGRESS" | "IN_REVIEW" | "REVISIONS_PENDING";
  to: "PENDING" | "IN_PROGRESS" | "IN_REVIEW" | "REVISIONS_PENDING";
  label: string;
  variant?: "outline" | "default" | "secondary" | "ghost";
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  async function onClick() {
    setBusy(true);
    const r = await postJson(`/api/admin/orders/${orderId}/status`, { method: "PATCH", body: { status: to, expectedStatus: from } });
    setBusy(false);
    if (!r.ok) {
      if (handleStaleResponse(r, navigate)) return;
      toast.error(r.error ?? "Could not change status");
      return;
    }
    toast.success(`Moved to ${label}`);
    navigate(0);
  }

  return (
    <Button onClick={onClick} disabled={busy} variant={variant} size="sm">
      {busy ? "…" : label}
    </Button>
  );
}
